import { useEffect, useRef, useState, type SyntheticEvent } from 'react'
import { guildBannerUrl } from '@/lib/avatar'
import styles from './DiscoveryPage.module.css'

/** Load near the viewport; animated banners stay on their first frame until the card is hovered. */
export function DiscoveryBanner({ guildId, hash, active = false, className = '' }: { guildId: string; hash: string; active?: boolean | undefined; className?: string | undefined }) {
  const url = guildBannerUrl(guildId, hash)
  const animated = hash.startsWith('a_')
  const box = useRef<HTMLDivElement>(null)
  const still = useRef<HTMLCanvasElement>(null)
  const [visible, setVisible] = useState(false)
  const [ready, setReady] = useState(false)
  const [broken, setBroken] = useState(false)
  const [canAnimate, setCanAnimate] = useState(false)

  useEffect(() => {
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)')
    const hover = window.matchMedia('(hover: hover)')
    const update = () => setCanAnimate(!motion.matches && hover.matches && document.visibilityState === 'visible')
    update()
    motion.addEventListener('change', update)
    hover.addEventListener('change', update)
    document.addEventListener('visibilitychange', update)
    return () => {
      motion.removeEventListener('change', update)
      hover.removeEventListener('change', update)
      document.removeEventListener('visibilitychange', update)
    }
  }, [])

  useEffect(() => {
    const el = box.current
    if (!el) return
    const observer = new IntersectionObserver(([entry]) => setVisible(Boolean(entry?.isIntersecting)), { rootMargin: '160px' })
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const play = active && canAnimate && visible
  const loaded = (event: SyntheticEvent<HTMLImageElement>) => {
    if (animated) {
      const img = event.currentTarget
      const canvas = still.current
      const context = canvas?.getContext('2d')
      if (!canvas || !context) { setBroken(true); return }
      canvas.width = img.naturalWidth
      canvas.height = img.naturalHeight
      context.drawImage(img, 0, 0)
    }
    setReady(true)
  }

  return <div ref={box} className={`${styles.banner} ${className}`} aria-hidden="true" data-loaded={ready && !broken ? '' : undefined}>
    {!broken && animated ? <canvas ref={still} className={ready && !play ? undefined : styles.mediaHidden} /> : null}
    {!broken && visible && (!animated || !ready || play) ? <img src={url} alt="" draggable={false} decoding="async" onLoad={loaded} onError={() => setBroken(true)} className={animated && !ready ? styles.mediaHidden : undefined} /> : null}
  </div>
}
